import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Rol bazlı varsayılan izinler
  const getDefaultPermissions = (role) => {
    const permissions = {
      'yonetici': {
        apps: { view: true, create: true, edit: true, delete: true },
        analytics: { view: true, export: true },
        team: { view: true, manage: true },
        reports: { view: true, generate: true }
      },
      'alt_yonetici': {
        apps: { view: true, create: true, edit: true, delete: false },
        analytics: { view: true, export: true },
        team: { view: true, manage: false },
        reports: { view: true, generate: true }
      },
      'gelistirici': {
        apps: { view: true, create: false, edit: true, delete: false },
        analytics: { view: true, export: false },
        team: { view: false, manage: false },
        reports: { view: true, generate: false }
      },
      'analizci': {
        apps: { view: true, create: false, edit: false, delete: false },
        analytics: { view: true, export: true },
        team: { view: false, manage: false },
        reports: { view: true, generate: true }
      },
      'pazarlamaci': {
        apps: { view: true, create: false, edit: false, delete: false },
        analytics: { view: true, export: false },
        team: { view: false, manage: false },
        reports: { view: true, generate: false }
      }
    };
    return permissions[role] || permissions['pazarlamaci'];
  };

  // Axios interceptor for token and rate limiting
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Rate limiting interceptor
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 429) {
          console.log('Rate limit hit, waiting 2 seconds...');
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Retry the request once
          try {
            return await axios.request(error.config);
          } catch (retryError) {
            console.log('Retry failed:', retryError.message);
            return Promise.reject(retryError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  // API base URL - Hardcode for now
  const apiUrl = 'https://cezecdev.netlify.app/api';

  axios.defaults.baseURL = apiUrl;
  console.log('API URL:', apiUrl);

  // Sonsuz döngüyü önlemek için ref kullanıyoruz
  const hasCheckedAuth = useRef(false);

  // Check if user is logged in on app start - SADECE BİR KEZ
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && !user && !hasCheckedAuth.current) {
        hasCheckedAuth.current = true;
        try {
          const response = await axios.get('/auth/me');
          if (response.data.success) {
            const userData = response.data.user;
            const userWithPermissions = {
              ...userData,
              permissions: userData.permissions || getDefaultPermissions(userData.role)
            };
            setUser(userWithPermissions);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);

          // 429 hatası ise token'ı silme, sadece user'ı null yap
          if (error.response?.status === 429) {
            console.log('Rate limit hatası, token korunuyor');
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [user]); // user dependency'sini ekledik ama ref ile kontrol ediyoruz

  const login = async (username, password) => {
    try {
      const response = await axios.post('/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data;

        // Kullanıcının izinlerini rol bazlı varsayılanlarla birleştir
        const userWithPermissions = {
          ...userData,
          permissions: userData.permissions || getDefaultPermissions(userData.role)
        };

        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userWithPermissions);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Giriş yapılırken bir hata oluştu'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const createUser = async (userData) => {
    try {
      const response = await axios.post('/auth/create-user', userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Kullanıcı oluşturulurken hata oluştu');
    }
  };

  const value = {
    user,
    login,
    logout,
    createUser,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};